import { Component, Input, OnChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DisplayCurrency } from '@lib/util';

@Component({
    selector: 'demand-product-price-input',
    templateUrl: './demand-product-price-input.component.html',
    styleUrls: ['./demand-product-price-input.component.scss'],
    standalone: false
})
export class DemandProductPriceInputComponent implements OnChanges {
    @Input() public formGroup!: UntypedFormGroup;
    @Input() public activeCurrencies!: DisplayCurrency[];
    /**
     * ISO codes the customer (Mandant) does not allow (EPROC-11425). Hidden from the dropdown and
     * named in a hint below the field with the customer as the reason.
     */
    @Input() public customerExcludedCurrencies: Set<string> = new Set<string>();
    /**
     * ISO codes the customer allows but at least one supplier of the demand does not accept
     * (EPROC-11425). Hidden from the dropdown and named in a hint below the field with the suppliers
     * as the reason.
     *
     * <p>An already set value survives being filtered out: `IimAdvancedAutocompleteComponent.initValue`
     * falls back to the written value when it finds no matching option
     * (`ctrl.setValue(matchedValue?.value ?? value)`) and renders it via `displayWith()`, so the
     * position keeps showing its currency. The conflict banner and the order-conversion block handle
     * it from there.
     */
    @Input() public supplierExcludedCurrencies: Set<string> = new Set<string>();

    /**
     * A hint names at most this many currencies: a tenant that maintains two currencies excludes
     * every other active one, and ~170 names below a price field are noise, not information.
     */
    private static readonly MAX_NAMED_CURRENCIES = 8;

    /** Currencies offered in the dropdown. */
    public selectableCurrencies: DisplayCurrency[] = [];
    /**
     * True when every active currency is excluded — the empty customer-supplier intersection.
     * Blocks deliberately (EPROC-11425, decision 06.08.2026); the hint names the way out.
     */
    public noCurrencySelectable = false;
    public customerExcludedCurrencyNames = '';
    /** How many customer-excluded currencies the hint summarizes instead of naming; 0 when none. */
    public customerExcludedOverflow = 0;
    public supplierExcludedCurrencyNames = '';
    /** How many supplier-excluded currencies the hint summarizes instead of naming; 0 when none. */
    public supplierExcludedOverflow = 0;

    /**
     * Derives the dropdown content and the hints once per input change. Deliberately not getters: the
     * template would re-filter and re-join on every change-detection run, and the parent hands in a
     * fresh Set whenever the lookup returns.
     */
    public ngOnChanges(): void {
        this.selectableCurrencies = (this.activeCurrencies ?? []).filter(
            (currency) =>
                !this.customerExcludedCurrencies.has(currency.iso) &&
                !this.supplierExcludedCurrencies.has(currency.iso),
        );
        this.noCurrencySelectable = !!this.activeCurrencies?.length && this.selectableCurrencies.length === 0;

        const customerNames = this.displayNamesOf(this.customerExcludedCurrencies);
        this.customerExcludedCurrencyNames = this.namesToHint(customerNames);
        this.customerExcludedOverflow = this.overflowOf(customerNames);

        const supplierNames = this.displayNamesOf(this.supplierExcludedCurrencies);
        this.supplierExcludedCurrencyNames = this.namesToHint(supplierNames);
        this.supplierExcludedOverflow = this.overflowOf(supplierNames);
    }

    private displayNamesOf(excluded: Set<string>): string[] {
        return (this.activeCurrencies ?? [])
            .filter((currency) => excluded.has(currency.iso))
            .map((currency) => currency.valueDisplay);
    }

    private namesToHint(names: string[]): string {
        return names.slice(0, DemandProductPriceInputComponent.MAX_NAMED_CURRENCIES).join(', ');
    }

    private overflowOf(names: string[]): number {
        return Math.max(0, names.length - DemandProductPriceInputComponent.MAX_NAMED_CURRENCIES);
    }
}
